import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Checkbox } from '@ipc/ipc-components';
import { Redirect, Link } from '@ipc/ipc-core';

import { createPreSettlement } from '../../api';
import { Loading } from '../../elements';
import { AppContentInner, AppContentTitle } from '../../styles';

import MainInfo from './sections/MainInfo';
import ScheduledType from './sections/ScheduledType';
import ImmediateType from './sections/ImmediateType';
import CarrierOperator from './sections/CarrierOperator';
import Receptacles from './sections/Receptacles';
import { EMPTY_BAG_VALUE, REQUEST_TYPE_OPTIONS } from './constants';
import { CheckboxWrapper } from './styles';
import { useFieldValues, useValidations } from './hooks';
import { formatDate, parseDate } from '../../utils/date';

const CreatePreSettlement = ({ match: { params: { id } }, newWarning }) => {
  const [saved, updateSaved] = useState(false);
  const [submitted, updateSubmitted] = useState(false);
  const [loading, updateLoading] = useState(false);
  const [receptacles, setReceptacles] = useState('all');
  const [isAllMailClassSelected, setAllMailClassSelected] = useState(false);
  const [isImpcSelected, setImpcSelected] = useState(false);
  const [values, setValues, options, inputChange, selectChange, dateChange, optionsLoaded] = useFieldValues(id, true, setAllMailClassSelected, setImpcSelected);
  const [validations] = useValidations(values, receptacles, submitted);

  useEffect(() => {
    if ((values.impc && values.impc.length) === (options.impc && options.impc.length)) {
      setImpcSelected(true);
    } else {
      setImpcSelected(false);
    }
  }, [values.impc && values.impc.length]);

  const receptaclesChangeHandler = (e) => {
    const newValue = e.target.value;
    const clearFields = newValue !== 'all' ? {
      requestType: REQUEST_TYPE_OPTIONS[0],
      dateType: null,
      mailClass: null,
      operator: null,
      carrier: null,
    } : {};
    if (newValue !== 'all') {
      setAllMailClassSelected(false);
    }

    setReceptacles(newValue);

    setValues({
      ...values,
      ...clearFields,
      consignmentId: undefined,
      despatchId: undefined,
      receptacleId: undefined,
    });
  };

  const checkboxChangeHandler = ({ target: { name } }, value) => {
    setValues({
      ...values,
      [name]: value,
    });
  };

  const allMailClassCheckboxHandler = (event, value) => {
    setAllMailClassSelected(value);
    setValues({
      ...values,
      mailClass: value ? options.mailClass.filter(item => item.value !== EMPTY_BAG_VALUE) : [],
    });
  };

  const allImpcCheckBoxHandler = (event, value) => {
    setImpcSelected(value);
    setValues({
      ...values,
      impc: value ? options.impc : [],
    });
  };
  const hasImpcOptions = () => Array.isArray(options.impc) && options.impc.length > 0;
  const disabled = receptacles !== 'all';
  const submitHandler = async () => {
    updateSubmitted(true);

    if (!validations.all) {
      return newWarning('Please fill all required fields and try again!', 5);
    }

    if (values.requestType.value === 'IMMEDIATE' && parseDate(values.endDate) > parseDate(formatDate(new Date()))) {
      return newWarning('End date should be in the past or current date');
    }

    if (values.requestType.value === 'SCHEDULED' && parseDate(values.startDate) < parseDate(formatDate(new Date()))) {
      return newWarning('Start date should be a future date or current date');
    }

    const dateRegEx = /^(\d{2}[/]\d{2}[/]\d{4})$/;
    if (!dateRegEx.test(values.startDate)) {
      return newWarning('Invalid Start Date value');
    }

    if (values.endDate && parseDate(values.startDate) > parseDate(values.endDate)) {
      return newWarning('Invalid Date range');
    }

    const updatedValues = disabled ? {
      consignmentId: values.consignmentId,
      receptacleId: values.receptacleId,
      despatchId: values.despatchId,
      excludeClosedTransit: values.excludeClosedTransit,
      requestName: values.requestName,
      requestType: values.requestType ? values.requestType.value : null,
      impc: values.impc,
    } : {
      ...values,
      dateType: values.dateType,
      operator: values.operator,
      mailClass: values.mailClass,
      carrier: values.carrier,
      requestType: values.requestType ? values.requestType.value : null,
      frequency: values.frequency ? values.frequency.value : null,
      runDay: values.runDay ? values.runDay.value : null,
      impc: values.impc,
    };

    updateLoading(true);

    const resp = await createPreSettlement(updatedValues);
    if (resp !== undefined) {
      updateSaved(true);
    }

    return updateLoading(false);
  };
  return saved
    ? (
      <Redirect to="/pre-settlement/list" />
    ) : (
      <AppContentInner>
        <AppContentTitle>Create Settlement Request</AppContentTitle>
        <form className="Form" onSubmit={e => e.preventDefault()}>
          <Loading show={!optionsLoaded || loading} />
          <MainInfo values={values} onInputChange={inputChange} onSelectChange={selectChange} disabled={disabled} validations={validations} />
          <br />
          {values.requestType && values.requestType.value === 'SCHEDULED' ? (
            <ScheduledType values={values} options={options} onDateChange={dateChange} onSelectChange={selectChange} validations={validations} />
          ) : (
            <ImmediateType values={values} options={options} onDateChange={dateChange} onSelectChange={selectChange} disabled={disabled} validations={validations} />
          )}
          <br />
          <CarrierOperator
            values={values}
            options={options}
            onSelectChange={selectChange}
            disabled={disabled}
            validations={validations}
            mailClassDisabled={!!isAllMailClassSelected}
            allMailClassCheckboxHandler={allMailClassCheckboxHandler}
            impcChecked={!!isImpcSelected}
            allImpcCheckBoxHandler={allImpcCheckBoxHandler}
            impcCheckboxDisableHandler={!hasImpcOptions()}
          />
          <br />
          <Receptacles values={values} onInputChange={inputChange} onReceptaclesChange={receptaclesChangeHandler} receptacles={receptacles} validations={validations} />

          <div className="ButtonWrapper">
            <CheckboxWrapper>
              <Checkbox
                label="Exclude Closed Transit Receptacles"
                name="excludeClosedTransit"
                checked={!!values.excludeClosedTransit}
                onChange={checkboxChangeHandler}
              />
            </CheckboxWrapper>
            <Link to="/pre-settlement/list">
              <Button className="CancelButton">Cancel</Button>
            </Link>
            <Button className="SaveButton" onClick={submitHandler} disabled={loading} data-testid="submit-action">Submit</Button>
          </div>
        </form>
      </AppContentInner>
    );
};

CreatePreSettlement.propTypes = {
  match: PropTypes.shape(),
  newWarning: PropTypes.func,
};

export default CreatePreSettlement;
